import { None } from "./types";
import {
	DictionaryConsumer,
	DictionaryProvider,
	DictionaryExpression,
	DynamicDictionaryExpression,
} from "./Dictionary";
import { ScalarProvider, ScalarConsumer, Scalar, scalarValue } from "./Scalar";
import { MutableVariable } from "./Variable";

class Entry<KeyType, ValueType> extends ScalarProvider<ValueType> implements ScalarConsumer<ValueType | None> {
	private readonly _subscribers = new Set<ScalarConsumer<ValueType>>();
	private _value: ValueType | None = None;

	constructor(
		readonly source: ScalarProvider<ValueType | None>,
		public key: KeyType,
		private parent: Filter<KeyType, ValueType>
	) {
		super();
		source.subscribe(this);
	}

	get value() {
		return this._value as ValueType;
	}

	update(value: ValueType | None) {
		const previous_value = this._value;
		if (value === previous_value) return;
		this._value = value;
		if (value === None && previous_value !== None) {
			this.parent.removeFromResult(this.key);
		}
		if (value !== None && previous_value === None) {
			this.parent.addToResult(this.key, this);
		}
		if (value !== None && previous_value !== None) {
			for (const subscriber of this._subscribers) {
				subscriber.update(value);
			}
		}
	}

	subscribe(subscriber: ScalarConsumer<ValueType>): typeof subscriber {
		this._subscribers.add(subscriber);
		subscriber.update(this.value);
		return subscriber;
	}

	unsubscribe(subscriber: ScalarConsumer<ValueType>) {
		this._subscribers.delete(subscriber);
	}

	destroy() {
		this.source.unsubscribe(this);
		if (this._value !== None) {
			this.parent.removeFromResult(this.key);
		}
	}
}

export class Filter<KeyType, ValueType> extends DictionaryExpression<
	KeyType,
	Scalar<ValueType | None>,
	Scalar<ValueType>
> {
	private _entries = new Map<KeyType, ValueType | None | Entry<KeyType, ValueType>>();

	constructor(source: DictionaryProvider<KeyType, Scalar<ValueType | None>>, readonly __origin?: string) {
		super(source);
	}

	addToResult(key: KeyType, value: Scalar<ValueType>) {
		this._subscriber!.add(key, value);
		this._size?.ensure(this._size.value + 1);
	}

	removeFromResult(key: KeyType) {
		this._subscriber!.remove(key);
		this._size?.ensure(this._size.value - 1);
	}

	add(key: KeyType, value: Scalar<ValueType | None>) {
		if (value instanceof ScalarProvider) {
			const entry = new Entry(value, key, this);
			this._entries.set(key, entry);
		} else {
			this._entries.set(key, value);
			if (value !== None) {
				this.addToResult(key, value);
			}
		}
	}

	remove(key: KeyType) {
		const value = this._entries.get(key)!;
		this._entries.delete(key);
		if (value instanceof Entry) {
			value.destroy();
		} else if (value !== None) {
			this.removeFromResult(key);
		}
	}

	get entries(): ReadonlyMap<KeyType, ValueType> {
		const entries = new Map<KeyType, ValueType>();
		for (const [key, entry] of this.source.entries) {
			const value = scalarValue(entry);
			if (value !== None) {
				entries.set(key, value);
			}
		}
		return entries;
	}

	private _size: MutableVariable<number> | undefined;
	get size(): ScalarProvider<number> {
		return (this._size ??= new MutableVariable(this.entries.size));
	}

	unsubscribe(subscriber: DictionaryConsumer<KeyType, Scalar<ValueType>>) {
		super.unsubscribe(subscriber);
		for (const entry of this._entries.values()) {
			if (entry instanceof Entry) entry.source.unsubscribe(entry);
		}
		this._entries.clear();
	}
}

export class DynamicFilter<KeyType, ValueType> extends DynamicDictionaryExpression<
	KeyType,
	Scalar<ValueType | None>,
	Filter<KeyType, ValueType>
> {
	constructor(
		source: ScalarProvider<DictionaryProvider<KeyType, Scalar<ValueType | None>>>,
		readonly __origin?: string
	) {
		super(source);
	}

	initialize() {
		return new Filter<KeyType, ValueType>(this.source.value);
	}
}
